"use client";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface SocialCardPreviewsProps {
  imageUrl: string;
  url: string;
  onImageClick?: () => void;
}

function extractDomain(url: string): string {
  try {
    let cleanUrl = url.trim();
    if (!cleanUrl.startsWith("http://") && !cleanUrl.startsWith("https://")) {
      cleanUrl = "https://" + cleanUrl;
    }
    return new URL(cleanUrl).hostname;
  } catch {
    return url;
  }
}

function FacebookPreview({
  imageUrl,
  domain,
  onImageClick,
}: {
  imageUrl: string;
  domain: string;
  onImageClick?: () => void;
}) {
  return (
    <div className="mx-auto w-[524px] max-w-full">
      <div className="bg-cover bg-center bg-no-repeat">
        <div
          className="relative aspect-[0.525] h-0 w-full cursor-pointer transition-opacity hover:opacity-90"
          style={{ paddingTop: "52.5%" }}
          onClick={onImageClick}
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={imageUrl}
            alt="Facebook preview"
            className="absolute top-0 block size-full object-cover"
          />
        </div>
      </div>
      <div className="break-words bg-[#F0F2F5] px-4 pb-1.5 pt-2.5">
        <div className="mb-[5px] overflow-hidden truncate whitespace-nowrap text-[13px] uppercase leading-[12px] text-[#65686C]">
          {domain}
        </div>
        <div className="block select-none overflow-hidden break-words text-left">
          <div className="truncate text-[17px] font-semibold leading-[21px] text-[#080809]">
            Your Page Title
          </div>
        </div>
      </div>
    </div>
  );
}

function TwitterLargeImagePreview({
  imageUrl,
  domain,
  onImageClick,
}: {
  imageUrl: string;
  domain: string;
  onImageClick?: () => void;
}) {
  return (
    <div className="[font-family:-apple-system,_BlinkMacSystemFont,_'Segoe_UI',_Roboto,_Helvetica,_Arial,_sans-serif]">
      <div className="relative mx-auto w-[438px] max-w-full overflow-hidden rounded-[0.85714em] border border-[#e1e8ed] leading-[1.3em] text-black -outline-offset-1">
        <div className="bg-cover bg-center bg-no-repeat">
          <div
            className="relative aspect-[0.525] h-0 w-full cursor-pointer transition-opacity hover:opacity-90"
            style={{ paddingTop: "52.33%" }}
            onClick={onImageClick}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={imageUrl}
              alt="Twitter large image preview"
              className="absolute top-0 block size-full object-cover"
            />
          </div>
        </div>
        <div className="absolute bottom-3 left-3 max-w-[calc(100%-1.5rem)] truncate rounded bg-black bg-opacity-[77%] px-2 text-[13px] text-white">
          Your Page Title
        </div>
      </div>
      <div className="mx-auto w-[438px] max-w-full text-[13px] text-[#536471]">
        From {domain}
      </div>
    </div>
  );
}

function LinkedInPreview({
  imageUrl,
  domain,
  onImageClick,
}: {
  imageUrl: string;
  domain: string;
  onImageClick?: () => void;
}) {
  return (
    <div className="mx-auto w-[526px] max-w-full overflow-hidden rounded-[8px] border border-[#8c8c8c33] bg-white p-3">
      <div className="flex items-stretch">
        <div
          className="relative h-[72px] w-[128px] shrink-0 cursor-pointer overflow-hidden rounded-[8px] transition-opacity hover:opacity-90"
          onClick={onImageClick}
        >
          <div className="relative aspect-[0.525] size-full">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={imageUrl}
              alt="LinkedIn preview"
              className="absolute top-0 block size-full object-cover"
            />
          </div>
        </div>
        <div className="flex min-w-0 flex-1 flex-col justify-center pl-3">
          <div className="mb-1 line-clamp-2 break-words text-[14px] font-semibold leading-[20px] text-[#000000e6]">
            Your Page Title
          </div>
          <div className="truncate text-[12px] font-normal leading-[16px] text-[#00000099]">
            {domain}
          </div>
        </div>
      </div>
    </div>
  );
}

function WhatsAppPreview({
  imageUrl,
  domain,
  onImageClick,
}: {
  imageUrl: string;
  domain: string;
  onImageClick?: () => void;
}) {
  return (
    <div className="mx-auto w-[340px] max-w-full overflow-hidden rounded-[7.5px] border border-[#e9edef] bg-[#daf0cb] font-[system-ui,_-apple-system,_BlinkMacSystemFont,_'Segoe_UI',_Helvetica,_Arial,_sans-serif]">
      <div className="bg-cover bg-center bg-no-repeat">
        <div
          className="w-full cursor-pointer transition-opacity hover:opacity-90"
          onClick={onImageClick}
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={imageUrl} alt="WhatsApp preview" className="block w-full" />
        </div>
      </div>
      <div className="px-3 py-2.5">
        <div className="mb-1.5 line-clamp-5 break-words text-[13px] font-semibold leading-[19px] text-[#111b21]">
          Your Page Title
        </div>
        <div className="mb-1.5 line-clamp-5 break-words text-[12px] font-medium leading-[17px] text-[#727f6e]">
          Your page description will appear here when shared on WhatsApp.
        </div>
        <div className="flex items-center justify-between gap-2">
          <div className="truncate text-[11px] font-medium leading-[16px] text-[#727f6e]">
            {domain}
          </div>
        </div>
      </div>
    </div>
  );
}

function DiscordPreview({
  imageUrl,
  domain,
  onImageClick,
}: {
  imageUrl: string;
  domain: string;
  onImageClick?: () => void;
}) {
  return (
    <div className="mx-auto w-[432px] max-w-full overflow-hidden rounded-[3px] border border-l-4 border-[#dfdfe1] bg-white">
      <div className="p-4 pb-0 pl-3">
        <div className="mb-4 flex flex-col gap-2">
          <div className="text-[12px] text-[#2c2d32]">{domain}</div>
          <div className="line-clamp-2 break-words text-[16px] font-semibold text-[#3a68db] hover:underline">
            Your Page Title
          </div>
          <div className="text-[14px] text-[#2c2d32]">
            Your page description will appear here when shared on Discord.
          </div>
        </div>
      </div>
      <div
        className="mx-3 mb-4 cursor-pointer overflow-hidden rounded-[3px] transition-opacity hover:opacity-90"
        onClick={onImageClick}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={imageUrl}
          alt="Discord preview"
          className="block h-auto max-h-[300px] w-full object-cover"
        />
      </div>
    </div>
  );
}

export function SocialCardPreviews({
  imageUrl,
  url,
  onImageClick,
}: SocialCardPreviewsProps) {
  const domain = extractDomain(url);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Social Media Previews</CardTitle>
        <CardDescription>
          See how your link will appear when shared on different platforms.
          Click on any preview to view full size.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="facebook" className="w-full">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="facebook">Facebook</TabsTrigger>
            <TabsTrigger value="twitter">X / Twitter</TabsTrigger>
            <TabsTrigger value="linkedin">LinkedIn</TabsTrigger>
            <TabsTrigger value="whatsapp">WhatsApp</TabsTrigger>
            <TabsTrigger value="discord">Discord</TabsTrigger>
          </TabsList>
          <TabsContent value="facebook" className="mt-6">
            <FacebookPreview
              imageUrl={imageUrl}
              domain={domain}
              onImageClick={onImageClick}
            />
          </TabsContent>
          <TabsContent value="twitter" className="mt-6">
            <TwitterLargeImagePreview
              imageUrl={imageUrl}
              domain={domain}
              onImageClick={onImageClick}
            />
          </TabsContent>
          <TabsContent value="linkedin" className="mt-6">
            <LinkedInPreview
              imageUrl={imageUrl}
              domain={domain}
              onImageClick={onImageClick}
            />
          </TabsContent>
          <TabsContent value="whatsapp" className="mt-6">
            <WhatsAppPreview
              imageUrl={imageUrl}
              domain={domain}
              onImageClick={onImageClick}
            />
          </TabsContent>
          <TabsContent value="discord" className="mt-6">
            <DiscordPreview
              imageUrl={imageUrl}
              domain={domain}
              onImageClick={onImageClick}
            />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
